import { defineStore } from 'pinia';
import API from '@/shared/helpers/API.js';
import {useUserStore} from "@/console-new/stores/user.store";
import {useAblyStore} from "@/console-new/stores/ably.store";

export const useNewsletterStore = defineStore('newsletter', {
	state: () => ({
		editingNewsletterId: null,
		businessNewsletters: null,
		conversationMessages: [],
		loadingConversation: false,
		currentNewsletterSubscription: null, // Track current subscription
		attachedImages: [], // Store image URLs for current message
		selectedSectionId: null, // Track selected section for editing
		thinkingMode: 'low', // Track thinking mode for newsletter generation
		previewMode: 'desktop', // Track preview mode: 'mobile' or 'desktop'
		newslettersStats: [],
		newslettersStatsTotal: 0,
		statusFilters: ['draft', 'in_review', 'sending', 'sent', 'scheduled'], // Track active status filters
	}),
	getters: {
		hasActiveFilters: (state) => {
			const allStatuses = ['draft', 'in_review', 'sending', 'sent', 'scheduled'];
			return state.statusFilters.length !== allStatuses.length;
		},
	},
	actions: {
		resetData() {
			this.editingNewsletterId = null;
			this.businessNewsletters = null;
			this.conversationMessages = [];
			this.loadingConversation = false;
			this.currentNewsletterSubscription = null;
			this.selectedSectionId = null;
		},
		getBusinessNewsletters(callback = null, forceRefresh = false) {
			if(this.businessNewsletters != null && !forceRefresh) { callback && callback(this.businessNewsletters); return; }

			let userStore = useUserStore();
			if(!userStore.currBusinessId) { return; }

			// Determine filters to send: null if all selected, empty array if none selected, otherwise the array of selected statuses
			const allStatuses = ['draft', 'in_review', 'sending', 'sent', 'scheduled'];
			let filtersToSend;
			if (this.statusFilters.length === allStatuses.length) {
				filtersToSend = null; // All selected - fetch all
			} else if (this.statusFilters.length === 0) {
				// None selected - return empty without API call
				this.businessNewsletters = [];
				callback && callback(this.businessNewsletters);
				return;
			} else {
				filtersToSend = this.statusFilters; // Some selected - fetch those
			}

			API.init().getNewsletters(userStore.currBusinessId, filtersToSend, response => {
				if (response && response.result === "OK") {
					if(response.newsletters && response.newsletters.length > 0) {
						this.normalizeNewsletters(response.newsletters);
						this.businessNewsletters = response.newsletters;
					} else {
						this.businessNewsletters = [];
					}
					callback && callback(this.businessNewsletters);
				}
			});
		},
		normalizeNewsletters(newsletters) {
			for(let i = 0; i < newsletters.length; i++) {
				newsletters[i].business_newsletter_id = Number(newsletters[i].business_newsletter_id);
				newsletters[i].business_id = Number(newsletters[i].business_id);
			}
		},
		getNewsletterConversation(newsletterId, silent = false, callback = null) {
			let userStore = useUserStore();
			if(!userStore.currBusinessId || !newsletterId) {
				callback && callback([]);
				return;
			}

			if (!silent) {
				this.loadingConversation = true;
				this.conversationMessages = [];
			}

			API.init().getNewsletterConversation(userStore.currBusinessId, newsletterId, response => {
				this.loadingConversation = false;
				if (response && response.result === "OK") {
					this.conversationMessages = response.messages || [];
					callback && callback(this.conversationMessages);
				} else {
					if (!silent) {
						this.conversationMessages = [];
					}
					callback && callback([]);
				}
			});
		},
		getNewsletter(newsletterId, callback = null) {
			const userStore = useUserStore();
			if (!userStore.currBusinessId || !newsletterId) {
				callback && callback(null);
				return;
			}

			API.init().getNewsletter(userStore.currBusinessId, newsletterId, response => {
				if (response && response.result === "OK" && response.newsletter) {
					// Normalize the newsletter first
					const newsletters = [response.newsletter];
					this.normalizeNewsletters(newsletters);
					const normalizedNewsletter = newsletters[0];

					// Update the newsletter in the list if it exists
					if (this.businessNewsletters) {
						const index = this.businessNewsletters.findIndex(n => n.business_newsletter_id === Number(newsletterId));
						if (index !== -1) {
							// Update all fields from the normalized newsletter
							Object.assign(this.businessNewsletters[index], normalizedNewsletter);
						}
					}
					callback && callback(normalizedNewsletter);
				} else {
					callback && callback(null);
				}
			});
		},
		subscribeToNewsletterUpdates(newsletterId, callback = null) {
			const userStore = useUserStore();
			const ablyStore = useAblyStore();

			if (!userStore.currBusinessId || !newsletterId || !ablyStore.ably) {
				console.log('Cannot subscribe to newsletter updates - missing business, newsletter, or ably not initialized');
				return;
			}

			// Unsubscribe from previous newsletter if any
			if (this.currentNewsletterSubscription) {
				this.unsubscribeFromNewsletterUpdates();
			}

			const channelKey = ENVIRONMENT + ':newsletter:' + userStore.currBusinessId + ':' + newsletterId;
			this.currentNewsletterSubscription = channelKey;

			ablyStore.subscribeToChannel(channelKey, this.newsletterUpdatesListener, callback);
		},
		unsubscribeFromNewsletterUpdates() {
			const ablyStore = useAblyStore();

			if (this.currentNewsletterSubscription && ablyStore.ably) {
				ablyStore.unsubscribeFromChannel(this.currentNewsletterSubscription);
				this.currentNewsletterSubscription = null;
			}
		},
		newsletterUpdatesListener(message) {
			if (message.name === 'conversation-update') {
				// Refresh the conversation messages silently (no loader flash)
				if (this.editingNewsletterId) {
					this.getNewsletterConversation(this.editingNewsletterId, true);
				}
			} else if (message.name === 'newsletter-update') {
				// Refresh the newsletter data (for MJML preview)
				if (this.editingNewsletterId) {
					this.getNewsletter(this.editingNewsletterId);
				}
			} else if (message.name === 'newsletter-status-update') {
				// Handle status-only updates
				if (this.editingNewsletterId && message.data && message.data.status) {
					// Update status in the business newsletters list
					if (this.businessNewsletters) {
						const newsletter = this.businessNewsletters.find(
							n => n.business_newsletter_id === this.editingNewsletterId
						);
						if (newsletter) {
							newsletter.status = message.data.status;
						}
					}
				}
			}
		},
		rollbackNewsletter(newsletterId, callback = null) {
			const userStore = useUserStore();
			if (!userStore.currBusinessId || !newsletterId) {
				callback && callback({ result: 'KO', details: 'Missing parameters' });
				return;
			}

			API.init().rollbackNewsletter(userStore.currBusinessId, newsletterId, response => {
				if (response && response.result === 'OK') {
					// Refresh the conversation to show the rollback message
					this.getNewsletterConversation(newsletterId, true);
					// Refresh the newsletter to update the preview
					this.getNewsletter(newsletterId);
					callback && callback(response);
				} else {
					callback && callback(response);
				}
			});
		},
		getFilteredNewslettersStats(filters, callback = null) {
			const userStore = useUserStore();
			if (!userStore.currBusinessId) {
				callback && callback(false);
				return;
			}

			API.init().getFilteredNewslettersStats(
				userStore.currBusinessId,
				JSON.stringify(filters || {}),
				r => {
					this.newslettersStats = [];
					this.newslettersStatsTotal = 0;

					if (r && r.result === "OK") {
						if (r.newsletters && r.newsletters.selected) {
							this.newslettersStats = r.newsletters.selected;
							this.newslettersStatsTotal = Number(r.newsletters.selected_num || 0);
						}
						if (callback) { callback(true); }
					} else {
						if (callback) { callback(false, r ? r.details : null); }
					}
				}
			);
		}
	}
});
